/**
 * EditPreview - Preview da mensagem sendo editada.
 * Aparece acima do input quando o usuário está editando uma mensagem.
 */
import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { ChatMessage } from '../../hooks/types';
import { getEditableMessageBody } from '../../../../utils/messageSignature';
import { formatMessageText } from '../../../../utils/sanitize';
import { getMessageCaption } from './useMessageCaption';
import { chatMediaIconName } from './media/MediaFallback';

interface EditPreviewProps {
    message: ChatMessage;
    onCancel: () => void;
}

function getEditPreviewText(message: ChatMessage): string {
    const raw = message.type === 'text' ? message.text : getMessageCaption(message, true, false);
    const body = getEditableMessageBody(raw);
    return body || message.type;
}

export const EditPreview: React.FC<EditPreviewProps> = ({ message, onCancel }) => {
    const hasMedia = message.type !== 'text';
    const mediaIcon = chatMediaIconName(message.type);
    const previewHtml = formatMessageText(getEditPreviewText(message));

    return (
        <div
            className="flex items-start gap-s3 border-b border-border bg-surface-1 px-s4 py-s2"
            role="status"
            aria-live="polite"
        >
            <div className="w-1 shrink-0 self-stretch rounded-full bg-success-500" aria-hidden="true" />

            <div className="flex shrink-0 items-center self-center text-success-text" aria-hidden="true">
                <AppIcon name="edit" className="size-5" />
            </div>

            <div className="min-w-0 flex-1">
                <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-success-text">
                    Editando mensagem
                </p>
                <p className="flex min-w-0 items-center gap-1 truncate text-base text-secondary">
                    {hasMedia && mediaIcon ? (
                        <AppIcon name={mediaIcon} className="size-3.5 shrink-0 opacity-70" aria-hidden="true" />
                    ) : null}
                    <span
                        className="min-w-0 truncate font-sans"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                </p>
            </div>

            <button
                type="button"
                onClick={onCancel}
                className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-subtle hover:text-secondary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                title="Cancelar edição"
                aria-label="Cancelar edição"
            >
                <AppIcon name="x" className="size-5" aria-hidden="true" />
            </button>
        </div>
    );
};
